import { Box } from "@mui/material";
import CustomTabs from "../components/CustomTabs";
import Icons from "../assets/Icons";
import Overview from "../modules/dashboardModule/components/Overview";
import { useEffect, useMemo, useRef, useState } from "react";
import MasterDataTable from "../modules/dashboardModule/components/MasterDataTable";
import ScreenTopBar from "../components/ScreenTopBar";
import { useFilterCount } from "../modules/dashboardModule/hooks/useFilterValue";
import DashboardFilterDrawer, {
  toggleDashboardFilterDrawer,
} from "../modules/dashboardModule/components/DashboardFilterDrawer";
import { useTranslation } from "react-i18next";
import useGetUserDetailQuery from "../modules/userModule/hooks/useGetUserDetailQuery";
import useAuthValue, {
  updateAuthValue,
} from "../modules/authModule/hooks/useAuthValue";
import useGetHospitalDetailQuery from "../modules/hospitalModule/hooks/useGetHospitalDetailQuery";
import OTByOTAnalysisDashboard from "../modules/dashboardModule/components/OTByOTAnalysisDashboard";
import RoleBasedAnalysis from "../modules/dashboardModule/components/RoleBasedAnalysis";
import ExportFilterModal from "../modules/caseManagementModule/components/ExportFilterModal";
import { globalStyles } from "../styles/GlobalStyles";
const tabData = [
  { id: "overview", label: "Overview", icon: <Icons.Dashboard /> },
  {
    id: "ot-by-ot-analysis",
    label: "OT-by-OT Analysis",
    icon: <Icons.Graph />,
    tooltip: "Ot_By_Ot_Analysis_Tooltip",
  },
  {
    id: "role-based-analysis",
    label: "Role Based Analysis",
    icon: <Icons.RoleBasedAnalysis />,
    tooltip: "Role_Based_Analysis_Tooltip",
  },
  {
    id: "master-data-table",
    label: "Master Data Table",
    icon: <Icons.Analytics />,
    tooltip: "Master_Data_Table_Tooltip",
  },
];

const Dashboard = () => {
  const { t } = useTranslation();
  const filterCount = useFilterCount();
  const { userId, tenantId, hospitalId } = useAuthValue();

  const [activeTab, setActiveTab] = useState<string | number>("overview");
  const masterDataTableRef = useRef<any>(null);
  const overviewExportRef = useRef<any>(null);
  const otByOtExportRef = useRef<any>(null);
  const roleBasedExportRef = useRef<any>(null);

  const {
    isOverview,
    isOtByOtAnalysis,
    isMasterDataTable,
    isRoleBasedAnalysis,
  } = useMemo(() => {
    return {
      isOverview: activeTab === "overview",
      isOtByOtAnalysis: activeTab === "ot-by-ot-analysis",
      isMasterDataTable: activeTab === "master-data-table",
      isRoleBasedAnalysis: activeTab === "role-based-analysis",
    };
  }, [activeTab]);

  const handleExport = () => {
    if (isOverview) {
      overviewExportRef?.current?.exportOverviewScreenshot();
    } else if (isOtByOtAnalysis) {
      otByOtExportRef?.current?.exportOverviewScreenshot();
    } else if (isMasterDataTable) {
      masterDataTableRef?.current?.exportMasterDataTable();
    } else if (isRoleBasedAnalysis) {
      roleBasedExportRef?.current?.exportRoleBasedAnalysis();
    }
  };

  const { data: accountDetails } = useGetUserDetailQuery({
    userId: userId?.toString(),
    tenantId,
    hospitalId,
  });

  const { data: hospitalDetails } = useGetHospitalDetailQuery(hospitalId);

  useEffect(() => {
    if (accountDetails) {
      updateAuthValue({
        user: accountDetails,
      });
    }
  }, [accountDetails]);

  useEffect(() => {
    if (hospitalDetails) {
      updateAuthValue({
        hospital: hospitalDetails,
      });
    }
  }, [hospitalDetails]);

  return (
    <Box sx={globalStyles.colFlex1}>
      <ScreenTopBar
        title={t("Analytics")}
        rightButtonsArray={[
          {
            label: "Export",
            onClick: handleExport,
            icon: isMasterDataTable ? <Icons.Csv /> : <Icons.Pdf />,
          },
          {
            label: "Filters",
            onClick: toggleDashboardFilterDrawer,
            icon: <Icons.Filter />,
            isBadgeEnable: true,
            badgeCount: filterCount,
          },
        ]}
        showSaveButton={false}
      />
      <CustomTabs
        tabs={tabData}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <Box sx={globalStyles.colFlex1} px={2}>
        <Box sx={{ display: isOverview ? "block" : "none" }}>
          <Overview exportOverviewScreenshot={overviewExportRef} />
        </Box>

        <Box sx={{ display: isOtByOtAnalysis ? "block" : "none" }}>
          <OTByOTAnalysisDashboard exportOverviewScreenshot={otByOtExportRef} />
        </Box>

        <Box sx={{ display: isRoleBasedAnalysis ? "block" : "none" }}>
          <RoleBasedAnalysis exportRoleBasedScreenshot={roleBasedExportRef} />
        </Box>

        <Box sx={{ display: isMasterDataTable ? "block" : "none" }}>
          <MasterDataTable masterDataTableRef={masterDataTableRef} />
        </Box>
      </Box>

      <DashboardFilterDrawer />
      <ExportFilterModal />
    </Box>
  );
};

export default Dashboard;
